import { Check, Play, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TOURS_LIST } from '@/features/guia-interactiva/tours';
import { useToursDisponibles } from '@/features/guia-interactiva/hooks/use-pasos-filtrados';
import { useGuiaInteractivaContext } from './guia-interactiva-context';

interface IndiceToursDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Índice de tours disponibles para el usuario (filtrados por permisos).
 * Muestra estado visto/pendiente y permite relanzar cualquiera reutilizando
 * la única instancia de driver.js del provider.
 *
 * No existe un "TourRunner" separado: el lanzamiento va directo al
 * `lanzarTourPorCodigo` del context, que maneja navegación a la ruta del
 * tour + ejecución en la misma instancia compartida.
 */
export function IndiceToursDialog({ open, onOpenChange }: IndiceToursDialogProps) {
  // Los tours marcados como `soloContextual` (p.ej. editor de tecnología,
  // que vive en ruta paramétrica) no se listan acá: si se lanzaran desde
  // otra pantalla fallarían al no encontrar sus selectores. Siguen
  // accesibles desde el botón `?` de su propia pantalla.
  const toursVisibles = TOURS_LIST.filter(t => !t.soloContextual);
  const disponibles = useToursDisponibles(toursVisibles);
  const { status, lanzarTourPorCodigo } = useGuiaInteractivaContext();

  const vistos = new Set(status?.toursVistos ?? []);

  function handleLanzar(codigo: string) {
    onOpenChange(false);
    // Delay para que la animación de cierre del dialog termine antes de que
    // driver.js monte su overlay (así no quedan ambos overlays a la vez).
    setTimeout(() => {
      void lanzarTourPorCodigo(codigo, { bloqueante: false });
    }, 150);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="text-primary size-4" />
            Guías interactivas
          </DialogTitle>
          <DialogDescription>
            Tours cortos por pantalla que te muestran las acciones principales. Cada uno se abre
            automáticamente la primera vez que entras al menú; desde aquí puedes volver a verlos.
          </DialogDescription>
        </DialogHeader>

        {disponibles.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            No hay guías disponibles para los permisos de tu rol.
          </p>
        ) : (
          <ul className="divide-border -mx-1 flex max-h-[60vh] flex-col divide-y overflow-y-auto">
            {disponibles.map(tour => {
              const visto = vistos.has(tour.codigo);
              return (
                <li key={tour.codigo} className="flex items-center gap-3 px-1 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium">{tour.titulo}</p>
                      <Badge
                        variant={visto ? 'secondary' : 'default'}
                        className={cn(
                          'h-5 text-[10px] font-medium',
                          visto && 'bg-success/15 text-success border-transparent'
                        )}
                      >
                        {visto ? (
                          <>
                            <Check className="mr-1 size-3" />
                            Vista
                          </>
                        ) : (
                          'Pendiente'
                        )}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground mt-0.5 truncate text-xs">
                      {tour.descripcion}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant={visto ? 'outline' : 'default'}
                    onClick={() => handleLanzar(tour.codigo)}
                  >
                    <Play className="mr-1.5 size-3" />
                    {visto ? 'Volver a ver' : 'Iniciar'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
